import {
  CircleAlertIcon,
  ClockIcon,
  MessageCircleQuestionIcon,
  PauseIcon,
} from "lucide-react";
import type { VariantProps } from "class-variance-authority";

import { TwinLiveMark } from "@/components/app/twin/twin-live-mark";
import { Badge, type badgeVariants } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

/*
 * The one vocabulary for a session or run state, wherever it is shown: the
 * sessions list, the Details sheet, run diagnostics and metrics. A raw
 * status such as `waiting_for_input` never reaches the screen.
 */
export function sessionStatus(status: string | undefined): {
  label: string;
  variant: BadgeVariant;
} {
  const normalized = (status ?? "").toLowerCase();
  if (!normalized) return { label: "Loading", variant: "outline" };
  if (
    normalized === "waiting_for_input" ||
    normalized === "waiting_for_user" ||
    normalized === "waiting_for_handoff" ||
    normalized === "needs_input"
  ) {
    return { label: "Needs you", variant: "default" };
  }
  if (normalized === "running") return { label: "Running", variant: "info" };
  if (normalized === "queued" || normalized === "pending") {
    return { label: "Queued", variant: "warning" };
  }
  if (normalized === "cancelling") {
    return { label: "Stopping", variant: "warning" };
  }
  if (normalized === "failed" || normalized === "error") {
    return { label: "Failed", variant: "danger" };
  }
  if (normalized === "completed" || normalized === "done") {
    return { label: "Completed", variant: "success" };
  }
  if (normalized === "paused") return { label: "Paused", variant: "secondary" };
  if (normalized === "cancelled" || normalized === "canceled") {
    return { label: "Cancelled", variant: "outline" };
  }
  return {
    label: `${normalized.charAt(0).toUpperCase()}${normalized.slice(1).replaceAll("_", " ")}`,
    variant: "outline",
  };
}

export function SessionStatusBadge({
  status,
  className,
}: {
  status: string | undefined;
  className?: string;
}) {
  const state = sessionStatus(status);
  return (
    <Badge variant={state.variant} className={className}>
      {state.label}
    </Badge>
  );
}

/*
 * A session's state as a small mark beside its title, only when it asks for
 * a look: working, waiting to start, needing someone, paused or failed. A
 * finished or cancelled session shows nothing; the tooltip names the state.
 */
export function SessionStatusMark({
  status,
  className,
}: {
  status: string | undefined;
  className?: string;
}) {
  const state = sessionStatus(status);
  const icon =
    state.label === "Running" ? (
      <TwinLiveMark active className="size-3.5" />
    ) : state.label === "Queued" || state.label === "Stopping" ? (
      <ClockIcon className="size-3.5 text-muted-foreground" />
    ) : state.label === "Needs you" ? (
      <MessageCircleQuestionIcon className="size-3.5 text-primary" />
    ) : state.label === "Paused" ? (
      <PauseIcon className="size-3.5 text-muted-foreground" />
    ) : state.label === "Failed" ? (
      <CircleAlertIcon className="size-3.5 text-destructive" />
    ) : null;
  if (!icon) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={state.label}
            className={cn("inline-flex shrink-0 items-center", className)}
          />
        }
      >
        {icon}
      </TooltipTrigger>
      <TooltipContent>{state.label}</TooltipContent>
    </Tooltip>
  );
}
